import React, { useEffect, useRef, useState } from "react"
import { connect, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router"

import { Button } from "components/Button"
import { Card } from "components/Card/Card"
import { UiCarousel } from "components/Carousel/Index"
import { UiFooter } from "components/Carousel/Ui"
import { UiQuestionsLine } from "components/Question/Question"
import { Input } from "components/Input"
import Loader from "components/Loaders/Loader/Loader"
import { GenericModal } from "components/Modal/Modal"
import PreviousButton from "components/Previous/PreviousButton"
import { InsertCarerModal } from "containers/InsertCarer"
import { FINALE_PAGE, PROFILE_PAGE } from "core/constants"
import { languages } from "lib/languages"
import { PATH } from "models/survey/_paths"
import { FOR_PATIENT } from "models/survey/constants"
import { Carer, Gender } from "types/entity"
import { Dispatcher } from "types/redux"
import { GlobalStore, SurveyStore } from "types/store"

import actions from "./services/actions"
import {
  getCurrentPatientGender,
  getCurrentSelectedPatientId,
} from "./services/selector"
import {
  ACTION,
  Answer,
  QUESTION_TYPE,
  Reply,
  SurveyProps,
  Value,
} from "./types"
import dayjs from "dayjs"
import { Title } from "components/Title/Title"
import { cdn } from "core/cdn"
import { Keyboard } from "hocs/withKeyboard"

const DefaultReply = (props: { reply: Reply; onSelect: () => void }) => {
  return (
    <Button onClick={props.onSelect} wide="long" type={props.reply?.ui?.type}>
      {props.reply.title}
    </Button>
  )
}

const Index: React.FC<SurveyProps> = (props): JSX.Element => {
  const { current: question, answer: selectedItems, status } = props
  const [tooYoungModalVisible, setTooYoungModalVisible] = useState(false)
  const [insertCarerModalVisible, setInsertCarerModalVisible] = useState(false)
  const [keyboardValue, setKeyboardValue] = useState("")
  const currentPatientId = useSelector(getCurrentSelectedPatientId)
  const currentPatientGender = useSelector(getCurrentPatientGender)
  const client = useSelector((state: GlobalStore) => state.client);
  const [tooYoungOrOld, setTooYoungOrOld] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const patientIdToSelect = (location.state as { selectedPatientId?: number } | null)
    ?.selectedPatientId
  const autoSelectDone = useRef(false)

  useEffect(() => {
    if (props.current.value && question.type === QUESTION_TYPE.redirect) {
      navigate(props.current.value)
    }
  }, [props.current])

  useEffect(() => {
    switch (props.status) {
      case "finish":
        navigate(FINALE_PAGE)
        break
      case "cancel":
        props.reset()
        navigate(PROFILE_PAGE)
        break
      case "on_going":
        break
    }
  }, [props.status])

  useEffect(() => {
    // Retour de la création d'un proche : on le sélectionne pour enchainer sur la suite
    // du parcours, dès que la question "pour qui ?" contient sa réponse.
    if (!question || question.id !== PATH.patient) return
    if (autoSelectDone.current || !patientIdToSelect) return
    const reply = question.replies.find(({ value }) => value === patientIdToSelect)
    if (!reply) return
    autoSelectDone.current = true
    onSelect(reply)
    // question.replies est réassigné à chaque rafraichissement des proches,
    // question gardant lui la même référence.
  }, [question?.replies, patientIdToSelect])

  if (!question) return <Loader />

  const next = () => {
    props.next()
  }

  const checkIfTooYoungOrOld = (patientId: number) => {
    let birthdate;
    if (patientId === FOR_PATIENT) {
      birthdate = client.customer?.birthdate;
    } else {
      birthdate = client.relatives?.find(r => r.id === patientId)?.birthdate;
    }
    const tooYoungorTooOld = dayjs().diff(birthdate, 'year', true) < 16 || dayjs().diff(birthdate, 'year', true) > 76;
    if (tooYoungorTooOld) {
      setTooYoungOrOld(true)
    } else {
      next()
    }
  }

  const getEffectiveAction = (r: Reply) => {
    // On check si on est dans le parcours des lentilles et pas des lunettes (glassesRenewal / lensesRenewal)
    const reasonAnswer = props.answers.find(({ questionId }) => questionId === PATH.reason)
    const selectedReason = reasonAnswer?.value?.[0]
    const shouldOpenContactLensQuestions =
      question.id !== PATH.reason_details || selectedReason === languages.lensesRenewal
    return r.action === ACTION.subquestion && !shouldOpenContactLensQuestions
      ? ACTION.default
      : r.action
  }

  const onSelect = (r: Reply) => {
    const replyToSet: Reply = { ...r, action: getEffectiveAction(r) }

    if (r.action === ACTION.redirect) {
      navigate(r.value as string)
      return
    }
    if (replyToSet.action === ACTION.inability) {
      if (r.value === "relative_too_young") {
        setTooYoungModalVisible(true)
      }
      return
    }
    if (!question.multipleReplies) {
      // Réinitialise la question (réponses synthétiques ex. null ne sont pas dans question.replies).
      props.unset()
      props.set([replyToSet])
      if (question.id === PATH.patient) {
        checkIfTooYoungOrOld(replyToSet.value as number);
      } else {
        next()
      }
      return
    }
    props.set([replyToSet])
  }

  // "questions_line" step: each line keeps its own answer, under its own id
  const isQuestionsLine = question.ui?.view === "questions_line"
  const linesAnswers: Record<string, Value> = Object.fromEntries(
    (question.questions ?? []).map(({ id }) => [
      id,
      props.answers.find(({ questionId }) => questionId === id)?.value?.[0],
    ])
  )
  const onSelectLine = (lineId: string, reply: Reply) => {
    props.unset(undefined, lineId as PATH)
    props.set([reply], lineId as PATH)
  }

  const unSelect = (r: Reply) => {
    if (!question.multipleReplies) {
      const oldValue = question.replies.filter((old) => old.value === r.value)
      if (oldValue) {
        const replyToSet: Reply = { ...r, action: getEffectiveAction(r) }
        props.unset([r])
        props.set([replyToSet])
        if (question.id === PATH.patient) {
          checkIfTooYoungOrOld(r.value as number);
        } else {
          next()
        }
      }
    } else {
      props.unset([r])
    }
  }

  return (
    <>
      {
        tooYoungOrOld ? (
          <>
            <PreviousButton onClick={() => {
              setTooYoungOrOld(false)
            }} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", height: "100%", width: "70%", padding: "20px 0" }}>
              <img src={cdn("images/error_cross.svg")} alt="" />
              <Title size="medium" text={languages.tooYoungOrOld} />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-evenly", width: "100%" }}>
                <div style={{ maxWidth: "25%", textAlign: "center" }}>
                  <img src={cdn("images/optic/young_person_sad.svg")} alt="" />
                  <p style={{ fontSize: "30px" }}>
                    {languages.tooYoungSubtitle}
                  </p>
                </div>
                <div style={{ maxWidth: "25%", textAlign: "center" }}>
                  <img src={cdn("images/optic/old_person_sad.svg")} alt="" />
                  <p style={{ fontSize: "30px" }}>
                    {languages.tooOldSubtitle}
                  </p>
                </div>
              </div>
              <Button type="primary" onClick={() => navigate(PROFILE_PAGE)}>
                {languages.backToHome}
              </Button>
            </div>
          </>
        ) : (
          <>
            <PreviousButton onClick={props.previous} />
            <Card
              title={question.title}
              subtitle={question.subtitle}
              imgUrl={question.imgUrl}
              // TextTransition leaves a title re-entering mid-slide in "position: absolute":
              // the long title of this step then overlaps its lines
              doNotAnimateTitle={isQuestionsLine}
            >
              {question.replies.length ? (
                <UiCarousel
                  items={question.replies}
                  selected={selectedItems}
                  onSelect={onSelect}
                  unSelect={unSelect}
                  ui={question.ui}
                />
              ) : null}
              {isQuestionsLine && (
                <UiQuestionsLine
                  question={question}
                  answers={linesAnswers}
                  onSelect={onSelectLine}
                  hint={languages.askToOptician}
                />
              )}
              {question.informationText && (
                <div className="information-card">{question.informationText}</div>
              )}
              <UiFooter
                style={
                  question.id === PATH.inform_anamnesis
                    ? { paddingTop: 0 }
                    : isQuestionsLine
                    ? { marginTop: "120px", paddingTop: 0 }
                    : undefined
                }
                nextVisible={!!question.multipleReplies}
                nextDisabled={
                  isQuestionsLine
                    ? Object.values(linesAnswers).some((v) => v === undefined)
                    : !selectedItems.length
                }
                onFinish={props.next}
              >
                {question.defaultReply && (
                  <DefaultReply
                    reply={question.defaultReply}
                    onSelect={() => {
                      // default reply pass on the next direct
                      onSelect(question.defaultReply as Reply)
                    }}
                  />
                )}
              </UiFooter>
              {question.id === PATH.inform_anamnesis && (
                <div>
                  <span
                    className="small-link"
                    onClick={() => setInsertCarerModalVisible(true)}
                  >
                    {currentPatientId === FOR_PATIENT
                      ? languages.ImCaredForThisTlc(
                          currentPatientGender ?? Gender.MALE
                        )
                      : languages.ImCaredForThisTlc_relative(
                          currentPatientGender ?? Gender.MALE
                        )}
                  </span>
                  <InsertCarerModal
                    visible={insertCarerModalVisible}
                    onFinish={(carer: Carer) => {
                      setInsertCarerModalVisible(false)
                      next()
                    }}
                    onCancel={() => setInsertCarerModalVisible(false)}
                  />
                </div>
              )}
              {
                [PATH.contact_lens_type, PATH.contact_lens_brand].includes(question.id) && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 24 }}>
                    <div>
                      <span className="small-link" style={{ fontSize: "28px" }} onClick={() => onSelect({ title: languages.IDontKnow, value: null, action: question.subquestion ? ACTION.subquestion : ACTION.default })}>
                        {languages.IDontKnow}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontSize: "32px" }}>{languages.askToOptician}</span>
                    </div>
                  </div>
                )
              }
              <GenericModal
                visible={tooYoungModalVisible}
                title={languages.notSuitableTLC}
                acceptText={languages.IGotIt}
                onAccept={() => {
                  setTooYoungModalVisible(false)
                }}
                contentText={languages.notSuitableTLCContent}
              />
            </Card>
          </>
        )}
    </>
  )
}

const mapStateToProps = ({ survey }: { survey: SurveyStore }): SurveyProps => {
  const current = survey.current
  const answer: Value[] =
    survey.answers.find(({ questionId }: Answer) => questionId === current?.id)
      ?.value || []

  return {
    current,
    answer,
    status: survey.status,
    answers: survey.answers,
    questions: survey.questions,
  } as SurveyProps
}

const mapDispatchToProps = (dispatch: Dispatcher): Partial<SurveyProps> => {
  return {
    set: (replies_to_add: Reply[], id?: PATH) =>
      dispatch(actions.setStepReplies({ replies: replies_to_add, id })),
    unset: (replies_to_remove?: Reply[], id?: PATH) =>
      dispatch(actions.unsetStepReplies({ replies: replies_to_remove, id })),
    next: () => dispatch(actions.next()),
    previous: () => dispatch(actions.previous()),
    reset: () => dispatch(actions.reset()),
    cancel: () => dispatch(actions.cancel()),
    submit: (a: Answer[]) => dispatch(actions.submit(a)),
  } as Partial<SurveyProps>
}

export default connect(mapStateToProps, mapDispatchToProps)(Index)
